import moment from 'moment/moment';

const monthDatePrefixRegex = /^(\d{4})-(0[1-9]|1[0-2])/;
const canonicalMonthFormat = 'YYYY-MM-01';
const canonicalDateFormat = 'YYYY-MM-DD';
const displayFormat = 'MMM YYYY';

/**
 * Parses a string into a month date (YYYY-MM-01).
 * Supports formats: YYYY-MM, YYYY-MM-DD, ISO 8601 timestamps.
 * Uses preserve-timezone via moment.parseZone to retain the source calendar month.
 */
function getMonthFromRawString(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const monthPrefixMatch = monthDatePrefixRegex.exec(trimmed);
  if (monthPrefixMatch) {
    const year = monthPrefixMatch[1];
    const month = monthPrefixMatch[2];
    return `${year}-${month}-01`;
  }

  const parsed = moment.parseZone(trimmed, moment.ISO_8601, true);
  if (!parsed.isValid()) {
    return null;
  }

  const year = parsed.year();
  const month = String(parsed.month() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

/**
 * Parses a string into a day date (YYYY-MM-DD).
 * Supports formats: YYYY-MM-DD, ISO 8601 timestamps.
 * Uses preserve-timezone via moment.parseZone to retain the source calendar date.
 */
function getDateOnlyFromRawString(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = moment.parseZone(trimmed, moment.ISO_8601, true);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format(canonicalDateFormat);
}

/**
 * Parses a normalized month date into moment.Moment (UTC).
 * @returns null if invalid date
 */
function parseTimelineMonthDate(monthDate: string): moment.Moment | null {
  const normalizedMonthDate = getMonthFromRawString(monthDate);
  if (!normalizedMonthDate) {
    return null;
  }

  const parsed = moment.utc(normalizedMonthDate, canonicalDateFormat, true);
  return parsed.isValid() ? parsed : null;
}

/**
 * Normalizes input into a month date (YYYY-MM-01).
 * @param monthDateRaw - string, Date, null, or undefined
 * @returns YYYY-MM-01 or current month (fallback) for null/undefined/invalid data
 */
export function normalizeTimelineMonthDate(monthDateRaw: unknown): string {
  const fallback = moment.utc().startOf('month').format(canonicalMonthFormat);

  if (monthDateRaw === null || typeof monthDateRaw === 'undefined') {
    return fallback;
  }

  if (monthDateRaw instanceof Date) {
    if (Number.isNaN(monthDateRaw.getTime())) {
      return fallback;
    }

    return moment.utc(monthDateRaw).format(canonicalMonthFormat);
  }

  const normalized = getMonthFromRawString(String(monthDateRaw));
  return normalized ?? fallback;
}

/**
 * Normalizes input into a day date (YYYY-MM-DD).
 * @param dateRaw - string, Date, null, or undefined
 * @returns YYYY-MM-DD or undefined for null/undefined/invalid data
 *
 * Uses preserve-timezone (moment.parseZone) for strings
 * to retain the source calendar date regardless of timezone.
 */
export function normalizeRidershipDateOnly(dateRaw: unknown): string | undefined {
  if (dateRaw === null || typeof dateRaw === 'undefined') {
    return undefined;
  }

  if (dateRaw instanceof Date) {
    if (Number.isNaN(dateRaw.getTime())) {
      return undefined;
    }

    return moment.utc(dateRaw).format(canonicalDateFormat);
  }

  return getDateOnlyFromRawString(String(dateRaw)) ?? undefined;
}

/**
 * Returns a date (YYYY-MM-DD) N days ago from the current moment (UTC).
 * @param daysAgo - number of days ago (minimum 0)
 * @returns YYYY-MM-DD in UTC
 */
export function getRidershipDateOnlyDaysAgo(daysAgo: number): string {
  const normalizedDays = Number.isFinite(daysAgo) ? Math.max(0, Math.floor(daysAgo)) : 0;
  return moment.utc().startOf('day').subtract(normalizedDays, 'days').format(canonicalDateFormat);
}

/**
 * Returns a numeric value for sorting month dates.
 * @param monthDate - date in YYYY-MM-DD or YYYY-MM-01 format
 * @returns timestamp (ms) or Number.MAX_SAFE_INTEGER for invalid data
 */
export function getTimelineMonthSortValue(monthDate: string): number {
  const parsed = parseTimelineMonthDate(monthDate);
  return parsed?.valueOf() ?? Number.MAX_SAFE_INTEGER;
}

/**
 * Returns the year from a month date.
 * @param monthDate - date in YYYY-MM-DD or YYYY-MM-01 format
 * @returns year (number) or null for invalid data
 */
export function getTimelineMonthYear(monthDate: string): number | null {
  const parsed = parseTimelineMonthDate(monthDate);
  return parsed?.year() ?? null;
}

/**
 * Formats a month date into a human-readable string.
 * @param monthDate - date in YYYY-MM-DD or YYYY-MM-01 format
 * @returns 'MMM YYYY' (e.g., 'Oct 2024') or the original string for invalid data
 */
export function formatTimelineMonth(monthDate: string): string {
  const parsed = parseTimelineMonthDate(monthDate);
  return parsed?.format(displayFormat) ?? (monthDate || '');
}

/**
 * Formats a ridership filter period for CSV export.
 * Returns a month label when the range covers a whole calendar month,
 * otherwise returns a date range.
 */
export function formatRidershipMonthOrDateRange(fromDateRaw: unknown, toDateRaw: unknown): string {
  const fromDate = normalizeRidershipDateOnly(fromDateRaw);
  const toDate = normalizeRidershipDateOnly(toDateRaw);

  if (!fromDate && !toDate) {
    return '';
  }

  if (!fromDate) {
    return toDate ?? '';
  }

  if (!toDate) {
    return fromDate;
  }

  const from = moment.utc(fromDate, canonicalDateFormat, true);
  const to = moment.utc(toDate, canonicalDateFormat, true);
  const isSingleMonthRange = from.isValid()
    && to.isValid()
    && from.isSame(to, 'month')
    && from.isSame(from.clone().startOf('month'), 'day')
    && to.isSame(to.clone().endOf('month'), 'day');

  if (isSingleMonthRange) {
    return formatTimelineMonth(fromDate);
  }

  if (fromDate === toDate) {
    return fromDate;
  }

  return `${fromDate} to ${toDate}`;
}
